import { useEffect, useState } from "react";

import { FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import { useAtomValue } from "jotai";
import { ROUTES } from "library/routes";
import { BenefitsPage } from "pages/BenefitsPage/BenefitsPage";
import { Dashboard } from "pages/Dashboard/Dashboard";
import { EmployerPage } from "pages/EmployerPage/EmployerPage";
import { HomePage } from "pages/HomePage/HomePage";
import { NoClientPage } from "pages/NoClientPage/NoClientPage";
import { ProfilePage } from "pages/ProfilePage/ProfilePage";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { IStyledTheme } from "theme/types";

import AppHeader from "components/AppHeader/AppHeader";
import { AppLayout } from "components/AppLayout/AppLayout";
import { AppLogoSpinner } from "components/AppLogoSpinner/AppLogoSpinner";
import { ConsentGuardedRoute } from "components/ConsentGuardedRoute/ConsentGuardedRoute";
import useEmployee from "hooks/useEmployee";
import { useInitSelectClient } from "hooks/useInitSelectClient";
import { useResizeObserverDevErrorFix } from "hooks/useResizeObserverDevErrorFix";
import { useGetTaxonomies } from "state/useGetTaxonomies";

import "react-toastify/dist/ReactToastify.css";

import { darkModeAtom, selectedClientAtom, selectedFullThemeAtom } from "./store";

import { useChatSessionId } from "hooks/useChatSessionId";

function App() {
  useResizeObserverDevErrorFix();
  useInitSelectClient();
  useGetTaxonomies();
  useChatSessionId();
  useEmployee();

  const darkMode = useAtomValue(darkModeAtom);
  const selectedTheme = useAtomValue(selectedFullThemeAtom);

  const [appTheme, setAppTheme] = useState<IStyledTheme | null>(null);

  const [fluentTheme, setFluentTheme] = useState(darkMode ? webDarkTheme : webLightTheme);

  // "#115ea3"

  useEffect(() => {
    const fluentTheme = darkMode ? webDarkTheme : webLightTheme;
    const appTheme: IStyledTheme = darkMode ? selectedTheme?.dark : selectedTheme?.light;

    setAppTheme(appTheme);
    setFluentTheme({
      ...fluentTheme,
      colorBrandBackgroundHover: appTheme.palette.purpleDark,
      colorCompoundBrandBackgroundHover: appTheme.palette.purpleDark,
      colorNeutralForeground2BrandHover: appTheme.palette.purpleDark,
      colorNeutralForeground2BrandSelected: appTheme.palette.purpleDark,
      colorNeutralForeground3BrandHover: appTheme.palette.purpleDark,
      colorNeutralForeground3BrandSelected: appTheme.palette.purpleDark,
      colorCompoundBrandForeground1: appTheme.palette.purpleDark,
      colorBrandForeground1: appTheme.palette.purpleDark,
      colorBrandForegroundOnLight: appTheme.palette.purpleDark,
      colorBrandBackground: appTheme.palette.purpleDark,
      colorCompoundBrandBackground: appTheme.palette.purpleDark,
      colorBrandBackgroundStatic: appTheme.palette.purpleDark,
      colorNeutralStrokeAccessibleSelected: appTheme.palette.purpleDark,
      colorBrandStroke1: appTheme.palette.purpleDark,
      colorBrandStroke2Pressed: appTheme.palette.purpleDark,
      colorCompoundBrandStroke: appTheme.palette.purpleDark,
      colorCompoundBrandStrokeHover: appTheme.palette.purpleDark,
    });
  }, [darkMode, selectedTheme]);

  if (!appTheme) return;

  return (
    <div>
      <FluentProvider theme={fluentTheme}>
        <StyledThemeProvider theme={appTheme}>
          <Content />
        </StyledThemeProvider>
      </FluentProvider>
    </div>
  );
}

export default App;

const Content = () => {
  const selectedClient = useAtomValue(selectedClientAtom);
  const userWithoutClients = selectedClient === null;
  const { employeeData } = useEmployee();

  return (
    <BrowserRouter>
      {selectedClient === undefined && <AppLogoSpinner />}

      <AppHeader />
      {userWithoutClients ? (
        <NoClientPage />
      ) : employeeData ? (
        <>
          <AppLayout>
            <Routes>
              <Route
                path={ROUTES.HOME}
                element={
                  <ConsentGuardedRoute path={ROUTES.HOME}>
                    <HomePage />
                  </ConsentGuardedRoute>
                }
              />
              <Route path={ROUTES.PROFILE} element={<ProfilePage />} />

              <Route
                path={ROUTES.BENEFITS}
                element={
                  <ConsentGuardedRoute path={ROUTES.BENEFITS}>
                    <BenefitsPage />
                  </ConsentGuardedRoute>
                }
              />
              <Route
                path={ROUTES.EMPLOYER}
                element={
                  <ConsentGuardedRoute path={ROUTES.EMPLOYER}>
                    <EmployerPage />
                  </ConsentGuardedRoute>
                }
              />
              <Route
                path={ROUTES.DASHBOARD}
                element={
                  <ConsentGuardedRoute path={ROUTES.DASHBOARD}>
                    <Dashboard />
                  </ConsentGuardedRoute>
                }
              />

              <Route
                path={"*"}
                element={
                  <ConsentGuardedRoute path={"*"}>
                    <Navigate to={ROUTES.BENEFITS} replace />
                  </ConsentGuardedRoute>
                }
              />
            </Routes>
          </AppLayout>
        </>
      ) : (
        <AppLogoSpinner />
      )}
      <ToastContainer
        style={{
          zIndex: 9999999,
        }}
      />
    </BrowserRouter>
  );
};
